import { Icon } from "@iconify/react";
import {
  Delete,
  FavoriteBorderRounded,
  FavoriteOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import i18next, { t } from "i18next";
import {
  debounce,
  get,
  isEmpty,
  set,
  throttle,
  toNumber,
  uniqBy,
} from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "react-query";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import EditConfig from "../../components/analytics/EditConfig";
import ConfigAction from "../../components/analyticTable/ConfigAction";
import ConfigTabs, {
  ConfigTabsRef,
} from "../../components/analyticTable/ConfigTabs";
import Filters from "../../components/analyticTable/Filters";
import MaxRecordsBanner from "../../components/analyticTable/MaxRecordsBanner";
import { FilterParamsValue } from "../../components/analyticTable/MoreFilters";
import TrialAnalyticsBanner from "../../components/analyticTable/TrialAnalyticsBanner";
import Header from "../../components/dashboard/Header";
import SplitButton from "../../components/exams/SplitButton";
import DeleteModal from "../../components/modals/DeleteModal";
import { useAnalyticTable } from "../../hooks/useAnalyticTable";
import { useAsAdmin, VIEW_PREFIX } from "../../hooks/useAsAdmin";
import { useAuth } from "../../hooks/useAuth";
import { UseFileDownload } from "../../hooks/useFileDownload";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";
import { AnalyticsData } from "../../shared/types/analyticData.type";
import { AnalyticExport } from "../../shared/types/analyticExport.type";
import { TAnalyticParams } from "../../shared/types/analyticParameters.type";
import { TAnalyticsFilters } from "../../shared/types/analyticsFilters.type";
import { TAnalyticView } from "../../shared/types/analyticView.type";
import { TUser, TuserAccountType } from "../../shared/types/user.type";
import { RootState } from "../../store";
import { setReloadAnalytics } from "../../store/reducers/modal.reducer";
import { getPatloadForSavingView } from "../../transformer/SaveViewPayload";
import { updateUserField } from "../../store/reducers/user.reducer";
import { CALL_HANDLER_KEY } from "../trainings/ExamView";
import ExcelExports from "../../components/analytics/ExcelExports";
import { AnalyticTable } from "../../api/analyticTable";
import { useLocales } from "../../hooks/useLocales";

export type TAnalyticData = {
  track_surfaces: (number | null)[];
  hands: (number | null)[];
  training_types: (number | null)[];
  track_names: (number | null)[];
  track_conditions: (number | null)[];
  horse_shoeings: (number | null)[];
  horses: number[];
  length: number;
  analytics_view: string;
  riders: (number | null)[];
  harnessed_mounteds: (string | null)[];
  horse_ages: (number | null)[];
  trainers: (number | null)[];
  min_age: number | null;
  max_age: number | null;
  is_filtered: false;
  include_horses_out_of_training: boolean;
  search: {
    value: string;
    regex: boolean;
  };
  order: {
    column: number;
    dir: "asc" | "desc";
  };
  start: number;
  date_start: string | null;
  date_end: string | null;
  forced_insight_parameters?: string[];
  insight_value_criterias: FilterParamsValue[];
  only_reference_trainings: boolean;
};
export const initialStateAnalyticData: TAnalyticData = {
  start: 0,
  hands: [],
  horse_ages: [],
  horse_shoeings: [],
  horses: [],
  max_age: null,
  length: 100,
  min_age: null,
  analytics_view: "",
  riders: [],
  track_conditions: [],
  harnessed_mounteds: [],
  trainers: [],
  track_names: [],
  track_surfaces: [],
  training_types: [],
  is_filtered: false,
  include_horses_out_of_training: false,
  search: {
    regex: false,
    value: "",
  },
  order: {
    column: 0,
    dir: "desc",
  },
  date_start: null,
  date_end: null,
  insight_value_criterias: [],
  only_reference_trainings: false,
};

type TAnalyticsProps = {
  showHeader?: boolean;
  disableEdit?: boolean;
  selectedHorse?: number;
  date?: string;
  onTrainingIdsChange?: (ids: number[]) => void;
};

function Analytics({
  showHeader = true,
  selectedHorse,
  disableEdit = false,
  onTrainingIdsChange,
  date,
}: TAnalyticsProps) {
  const accountType = useSelector<RootState>(
    (state) => state.user.user?.type,
  ) as TuserAccountType | undefined;

  const [isInitialLoad, setIsInitialLoaded] = useState(true);

  const [view, setView] = useState<TAnalyticView | null>(null);
  const [render, setRender] = useState(false);
  const [recordsBanner, setRecordsBanner] = useState(false);
  const [analyticData, setAnalyticData] = useState<TAnalyticData>(
    initialStateAnalyticData,
  );

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | undefined>(
    undefined,
  );
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [selectedParams, setSelectedParams] = useState<TAnalyticParams[]>([]);
  const [searchedParams, setSearchedParams] = useState<TAnalyticParams[]>([]);
  const [selectedExportOption, setSelectedExportOption] = useState(0);
  const { currentLang } = useLocales();

  const [isCreatingNewView, setIsCreatingNewView] = useState(false);
  const lastSelectedView = useRef<TAnalyticView | null>(null);

  const configTabsRef = useRef<ConfigTabsRef>(null);

  const handleExternalViewSelect = useCallback((viewId: number) => {
    // Call the imperative handle method
    configTabsRef.current?.fetchView(viewId, true);
  }, []);
  const userAnalyticsViews = useSelector<RootState>(
    (state) => state.user.user?.analytics_views,
    shallowEqual,
  ) as Partial<TAnalyticView>[];
  const [view_name, setViewName] = useState("");
  //Hooks
  const isUserLoading = useSelector<RootState>(
    (state) => state.user.isUserLoading,
  ) as boolean;
  const user = useSelector<RootState>((state) => state.user.user) as TUser;
  const default_view = useSelector<RootState>(
    (state) => state.user.user?.default_analytics_view,
  ) as string;
  const dispatchReloadAnalytics = useSelector<RootState>(
    (state) => state.modals.dispatchReloadAnalytics,
  ) as boolean;

  const {
    fetchAnalyticData,
    exportAnalyticDataMutation,
    deleteView,
    updateView,
    setAnalyticViews,
    allParams,
    groupedParams,
    getParameters,
    saveNewConfig,
    getInsightParametersNames,
  } = useAnalyticTable(false, user?.["@id"], true);

  const {
    analyticViews: adminAnalyticViews,
    isAnalyticsViewsLoading,
    isConnectedAs,
  } = useAsAdmin({ queryEnabled: !isUserLoading && true });

  const { auth, getUserDateFormat, updateUser } = useAuth();
  const { download, isDownloadError, isDownloading } = UseFileDownload();
  const { track } = useUserActivityLog();

  const query = useQueryClient();
  const expirationDate = auth?.eap_trial_expiration_date;
  const eap_enabled = auth?.eap_access;
  const today_date = dayjs();

  const dispatch = useDispatch();

  const isTrialEnded =
    !eap_enabled &&
    (isEmpty(expirationDate) || dayjs(expirationDate).isBefore(today_date));
  const isTrialActif =
    !eap_enabled &&
    !isEmpty(expirationDate) &&
    dayjs(expirationDate).isAfter(today_date);

  const isLoading: boolean = isAnalyticsViewsLoading;

  // PERF: avoid querying/reading the DOM on every single render.
  // We compute these once on mount and keep them in sync via the
  // native 'fullscreenchange' event instead of re-reading on each render.
  const [analyticsEl, setAnalyticsEl] = useState<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isPending = (e: AnalyticExport) => e.status === 0 || e.status === 1;
  const [filters, setFilters] = useState<TAnalyticsFilters | undefined>(
    undefined,
  );
  const {
    data: filtersData,
    refetch: refetchFilters,
    isLoading: filtersLoading,
    isFetching,
  } = useQuery(
    ["analytics_filters", user?.id, selectedHorse],
    () =>
      AnalyticTable.getFilters(user!.id, selectedHorse, currentLang).then(
        (r) => r.data,
      ), // use the API fn your mutation wraps
    {
      enabled: !!user?.id,
      keepPreviousData: true,
      refetchInterval: (data) =>
        data?.previous_exports?.some(isPending) ? 10_000 : false,
      onSuccess: (data) => {
        setFilters(data);
      },
    },
  );

  const onExportSucceeded = async (e: AnalyticExport) => {
    await download(e.file_url);
  };

  useEffect(() => {
    const el = document.getElementById("analytics_content");
    setAnalyticsEl(el);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === analyticsEl);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    handleFullscreenChange();
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [analyticsEl]);

  const lang = useMemo(() => i18next.language, [i18next.language]);

  useEffect(() => {
    track("ANALYTICS_PAGE_DISPLAYED");
  }, []);

  const setAsFavourite = useCallback(() => {
    let value = default_view === view?.["@id"] ? null : view?.["@id"];

    updateUser({ default_analytics_view: value, id: auth.id });
  }, [default_view, view, updateUser, auth?.id]);

  //End of user exports

  //State of Config Modal
  const [pinnedParams, setPinnedParams] = useState<TAnalyticParams[]>([]);

  //Effects

  const saveViewPayload = useMemo(() => {
    if (!filters) return;
    return getPatloadForSavingView(analyticData, filters);
  }, [filters, analyticData]);

  useEffect(() => {
    setSearchedParams(allParams);
  }, [allParams.length]);

  useEffect(() => {
    getParameters.mutateAsync();
  }, []);

  useEffect(() => {
    setFilterForAnalytics();
    setRender(true);
  }, [view?.id, selectedHorse, isInitialLoad]);

  const AnalyticViews = useMemo(() => {
    return uniqBy(
      [...(userAnalyticsViews ?? []), ...(adminAnalyticViews ?? [])],
      "id",
    );
  }, [userAnalyticsViews, adminAnalyticViews]);

  const setFilterForAnalyticsImplRef = useRef<() => void>(null);
  setFilterForAnalyticsImplRef.current = () => {
    if (!view) return;

    let insight_critearia = view?.insight_value_criterias ?? [];

    setAnalyticData((prev) => ({
      ...prev,
      horses: selectedHorse
        ? [selectedHorse]
        : view.all_horses
          ? []
          : (view?.horses.map((e) => e.id) ?? []),
      track_conditions: [
        ...(view.all_track_conditions
          ? []
          : [
              ...(get(view, "track_conditions", []) || []).map((i) => i?.id),
              ...(get(view, "include_null_track_condition") ? [null] : []),
            ]),
      ],
      training_types: [
        ...(view.all_training_types
          ? []
          : [
              ...(get(view, "training_types", []) || []).map((i) => i?.id),
              ...(get(view, "include_null_training_type") ? [null] : []),
            ]),
      ],
      track_surfaces: [
        ...(view.all_track_surfaces
          ? []
          : [
              ...(get(view, "track_surfaces", []) || []).map((i) => i?.id),
              ...(get(view, "include_null_track_surface") ? [null] : []),
            ]),
      ],
      track_names: [
        ...(view.all_track_names
          ? []
          : [
              ...(get(view, "track_names", []) || []).map((i) => i?.id),
              ...(get(view, "include_null_track_name") ? [null] : []),
            ]),
      ],
      riders: [
        ...(view.all_riders
          ? []
          : [
              ...(get(view, "riders", []) || []).map((i) => i?.id),
              ...(get(view, "include_null_rider") ? [null] : []),
            ]),
      ],
      horse_ages: [
        ...(view.all_horse_ages
          ? []
          : [
              ...(get(view, "horse_ages", []) || []),
              ...(get(view, "include_null_horse_age") ? [null] : []),
            ]),
      ],
      hands: [
        ...(view.all_hand_turns
          ? []
          : [
              ...(get(view, "hand_turns", []) || []),
              ...(get(view, "include_null_hand") ? [null] : []),
            ]),
      ],
      trainers: [
        ...(view.all_trainers
          ? []
          : [
              ...(get(view, "trainers", []) || []).map((i) => i?.id),
              ...(get(view, "include_null_trainers") ? [null] : []),
            ]),
      ],
      ...(accountType === "TROT"
        ? {
            harnessed_mounteds: [
              ...(view.all_harnessed_mounteds
                ? []
                : [
                    ...(get(view, "harnessed_mounteds", []) || []),
                    ...(get(view, "include_null_harnessed_mounted")
                      ? [null]
                      : []),
                  ]),
            ],
          }
        : {}),

      include_horses_out_of_training: selectedHorse
        ? true
        : view.include_horses_out_of_training,
      only_reference_trainings: view.only_reference_trainings,
      date_start: null,
      date_end: null,
      insight_value_criterias: insight_critearia.map((e) => ({
        field: e.insight_parameter.name,
        operator: e.operator,
        value: e.value,
      })),
    }));
  };

  const setFilterForAnalytics = useMemo(
    () => throttle(() => setFilterForAnalyticsImplRef.current?.(), 300),
    [],
  );

  useEffect(() => {
    setAnalyticData((prev) => {
      // If date is empty and we aren't already null, update it
      if (!date) {
        if (prev.date_start === null) return prev; // Bail out to prevent loop
        return { ...prev, date_start: null, date_end: null };
      }

      // If date has changed, update it
      if (date !== prev.date_start) {
        return {
          ...prev,
          date_start: date,
          date_end: dayjs().toISOString(),
        };
      }

      return prev; // No changes, no re-render
    });
  }, [date]);

  const getAnalyticsPayload = useCallback(
    (analyticsIds: string[] = []) => {
      let analyticsPayload = { id: user.id, data: { ...analyticData } };

      if (view) {
        set(analyticsPayload.data, "analytics_view", view["@id"]);
      }
      if (!isEmpty(analyticsIds)) {
        set(analyticsPayload.data, "forced_insight_parameters", analyticsIds);
      }
      return analyticsPayload;
    },
    [user["@id"], view?.id, analyticData],
  );

  const getIdsAndFetchCharts = useCallback(
    (data: any) => {
      let rows = data?.data ?? ([] as string[][]);
      let ids: number[] = [];

      let spans = rows.map((e: string[]) => e[0]);

      //We get the id for data attribute with regex
      spans.forEach((span: string) => {
        const match = span.match(/data-training-id='(\d+)'/);

        if (match) {
          const trainingId = match[1];
          ids.push(toNumber(trainingId));
        }
      });

      if (typeof onTrainingIdsChange === "function") {
        onTrainingIdsChange(ids);
      }
    },
    [onTrainingIdsChange],
  );

  const fetchAnalyticsData = useCallback(
    (analyticsIds: string[] = []) => {
      const MAX_RECORDS = 1000;

      if (!view || !render) return;

      if (isInitialLoad && view?.default_analytics_data) {
        setAnalyticsData(view.default_analytics_data);

        setRecordsBanner(
          view.default_analytics_data.records_filtered >= MAX_RECORDS,
        );

        setIsInitialLoaded(false);
        return;
      }

      let analyticsPayload = getAnalyticsPayload(analyticsIds);

      fetchAnalyticData
        .mutateAsync(analyticsPayload)
        .then((response) => {
          setAnalyticsData(response.data);

          console.log({ data: response.data });

          let records_filtered: number = get(
            response,
            "data.records_filtered",
            0,
          );

          getIdsAndFetchCharts(response.data);

          //if records_filtered === 1000 show banner for search
          setRecordsBanner(records_filtered >= MAX_RECORDS);
        })
        .catch((err) => {
          toast(t("error:generic"), { type: "error" });
        })
        .finally(() => {
          dispatch(setReloadAnalytics(false));
        });
    },
    [
      view?.id,
      render,
      isInitialLoad,
      getAnalyticsPayload,
      fetchAnalyticData,
      getIdsAndFetchCharts,
      dispatch,
    ],
  );

  useEffect(() => {
    if (dispatchReloadAnalytics) {
      fetchAnalyticsData();
    }
  }, [dispatchReloadAnalytics]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [JSON.stringify(analyticData), view?.id, lang]);

  const setViewPinnedParam = useCallback((view: TAnalyticView) => {
    let count = view.count_columns_locked;
    if (count === 0) {
      setPinnedParams([]);
      return;
    }

    setPinnedParams(
      view.analytics_view_insight_parameters
        .map((e) => e.insight_parameter)
        .slice(0, count),
    );
  }, []);

  useEffect(() => {
    if (view) {
      setViewName(view.name);
      setViewPinnedParam(view);
    }
  }, [view?.id]);

  //We block the csm views edit if the view contain CSM PREFIX
  const isEditEnabled = useMemo(() => {
    let current_view = AnalyticViews.find((v) => v?.id === view?.id);
    if (current_view) {
      return !current_view?.name?.includes(VIEW_PREFIX);
    }
    return false;
  }, [view?.id, AnalyticViews]);

  const closeParamsModal = useCallback(() => {
    setOpenParamsModal(false);

    if (isCreatingNewView && lastSelectedView.current) {
      setViewName(lastSelectedView.current?.name ?? "");
      const insigtParams =
        lastSelectedView.current?.analytics_view_insight_parameters.map(
          (e) => e.insight_parameter,
        );

      // Filter Date and Horse from data
      const blacklist = ["training.date", "horse.name"];
      const filteredInsight = insigtParams?.filter(
        (item) => !blacklist.includes(item.name),
      );

      handleSetParams(filteredInsight ?? []);

      setViewPinnedParam(lastSelectedView.current);

      lastSelectedView.current = null;
    }
    setIsCreatingNewView(false);
  }, [isCreatingNewView]);

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setAnalyticData((prev) => ({
            ...prev,
            search: {
              regex: false,
              value: e.target.value,
            },
          }));
        },
        500,
      ),
    [],
  );

  const delView = useCallback(
    async (id: number) => {
      try {
        await deleteView.mutateAsync(id);

        setOpenDeleteModal(false);

        // 1. Filter out the deleted view from Redux userAnalyticsViews
        const updatedUserViews =
          userAnalyticsViews?.filter((view) => view?.id !== id) ?? [];

        dispatch(
          updateUserField({
            field: "analytics_views",
            value: updatedUserViews,
          }),
        );

        // 2. Fetch the next available view via the ref
        const nextViewId = updatedUserViews[0]?.id;
        if (nextViewId) {
          handleExternalViewSelect(nextViewId);
        }

        closeParamsModal();
      } catch (err) {
        toast(t("error:generic"), { type: "error" });
      }
    },
    [userAnalyticsViews, closeParamsModal],
  );

  const updateViewMutation = useCallback(
    async (data: any) => {
      if (!view) return;

      try {
        let result = await updateView.mutateAsync({ id: view?.id, data });
        setView(result.data);
        query.invalidateQueries(["view", view.id]);
        query.setQueryData(["view", view.id], result.data);
        if (view.name !== data?.name) {
          query.refetchQueries(["auth"]);
          if (isConnectedAs) {
            query.refetchQueries(["admin_analytics_view"]);
          }
        }
        fetchAnalyticsData();
        toast(t("rights:success"), { type: "success" });
        return result.data;
      } catch (error) {
        toast(t("error:generic"), { type: "error" });
        return error;
      }
    },
    [view?.id, fetchAnalyticsData],
  );

  const updateViewName = useCallback(
    async (name: string) => {
      if (!view) return;
      try {
        await updateViewMutation({ name, user: user?.["@id"] });
        query.refetchQueries(["auth"]);
        query.refetchQueries(["admin_analytics_view"]);
      } catch (error) {}
    },
    [view?.id, user.id],
  );

  const handleAnalyticsParams = (items: TAnalyticParams[]) => {
    if (!isEmpty(items)) {
      setSelectedParams(items);
      //Map id to update datatable to visualize new data
      let ids = items.map((param) => param["@id"]);
      fetchAnalyticsData(ids);
    }
  };

  const updateViewSetting = useCallback(() => {
    if (!view) return;

    let ids = selectedParams.map((e, i) => ({
      insight_parameter: e["@id"],
      order_index: i + 1,
    })) as any[];

    updateViewMutation({
      ...saveViewPayload,
      analytics_view_insight_parameters: ids,
    });
  }, [view?.id, selectedParams, saveViewPayload]);

  const onUpdateParam = useCallback((params: TAnalyticParams[]) => {
    setSelectedParams(params);
  }, []);

  const handleCustomValue = useCallback((values: FilterParamsValue[]) => {
    setAnalyticData((prev) => ({ ...prev, insight_value_criterias: values }));
  }, []);

  const updateFilters = useCallback((key: keyof TAnalyticData, value: any) => {
    setAnalyticData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const batchUpdateFilters = useCallback((data: any) => {
    setAnalyticData((prev) => ({ ...prev, ...data }));
  }, []);

  const onParamCheck = useCallback(
    (item: TAnalyticParams, checked: boolean) => {
      if (checked) {
        setSelectedParams((prev) => [...prev, item]);
      } else {
        let index = selectedParams.findIndex((e) => e.id === item.id);
        if (index !== -1) {
          setSelectedParams((prev) => prev.filter((e, i) => i !== index));
        }
      }
    },
    [selectedParams],
  );

  const saveParams = useCallback(() => {
    let ids = selectedParams.map((e, i) => ({
      insight_parameter: e["@id"],
      order_index: i + 1,
    }));
    let data = {
      analytics_view_insight_parameters: ids,
      count_columns_locked: pinnedParams.length,
      name: view_name ?? "unnamed configuration",
      user: user?.["@id"],
    } as any;

    if (isCreatingNewView) {
      saveNewConfig.mutateAsync(data).then((result) => {
        dispatch(
          updateUserField({
            field: "analytics_views",
            value: [...userAnalyticsViews, result.data],
          }),
        );
        // Fetch and switch to the newly created view
        toast(t("view:create_success"), { type: "success" });
        if (result?.data?.id) {
          handleExternalViewSelect(result.data.id);
        }

        closeParamsModal();
      });
      return;
    }
    if (!view) return;
    updateViewMutation(data).finally(() => {
      closeParamsModal();
    });
  }, [
    selectedParams,
    pinnedParams,
    view?.id,
    view_name,
    user?.["@id"],
    closeParamsModal,
    isCreatingNewView,
  ]);

  const onDuplicate = useCallback(() => {
    if (!view) return;
    let { analytics_view_insight_parameters = [] } = view;

    let userId = user["@id"];
    saveNewConfig
      .mutateAsync({
        name: `copy - ${view.name}`,
        analytics_view_insight_parameters:
          analytics_view_insight_parameters.map((e, index) => ({
            order_index: e.order_index,
            insight_parameter: e.insight_parameter["@id"],
          })),
        user: userId,
      })
      .then((result) => {
        query.refetchQueries(["auth"]);
        toast(t("rights:success"), { type: "success" });
      })
      .catch((err) => {
        toast(t("error:generic"), { type: "error" });
      })
      .finally(() => {
        setOpenParamsModal(false);
      });
  }, [view?.id, user?.id]);

  const onUpdatePin = useCallback((pins: TAnalyticParams[]) => {
    setPinnedParams(pins);
  }, []);

  const setSelectedViewData = useCallback(
    (view: TAnalyticView, params: TAnalyticParams[]) => {
      setView(view);
      setSelectedParams(params);
    },
    [],
  );

  const goFullScreen = useCallback(() => {
    let contentElement = document.querySelector("#analytics_content");

    if (contentElement) {
      contentElement.requestFullscreen({ navigationUI: "auto" });
    }
  }, []);

  // PERF: these were inline arrow functions created fresh every render and
  // passed straight down as props; memoizing them keeps referential
  // stability for memoized children without altering behavior.
  const openNewParamsModal = useCallback(() => {
    setOpenParamsModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setOpenDeleteModal(false);
  }, []);

  const openDeleteModalHandler = useCallback(() => {
    setOpenDeleteModal(true);
  }, []);

  const toggleDeleteModal = useCallback((value: boolean) => {
    setOpenDeleteModal(value);
  }, []);

  const handleUpdateViewName = useCallback(
    (name: string) => {
      updateViewName(name);
    },
    [updateViewName],
  );

  const handleSetParams = useCallback((params: TAnalyticParams[]) => {
    setSelectedParams(params);
  }, []);

  const handleSetViewName = useCallback((name: string) => {
    setViewName(name);
  }, []);

  const handleDeleteCurrentView = useCallback(() => {
    if (view) {
      delView(view.id);
    }
  }, [view, delView]);

  const setUserParams = useCallback(() => {
    const paramsNames = getInsightParametersNames(accountType);

    if (!isEmpty(paramsNames)) {
      //get Current params by name
      let filtered = allParams.filter((e) => paramsNames.includes(e.name));
      setSelectedParams(filtered);
      setPinnedParams([]);
    }
  }, [allParams.length, accountType]);

  const OnCreateNewView = () => {
    lastSelectedView.current = view;
    handleSetViewName("");
    setUserParams();
    setIsCreatingNewView(true);
    openNewParamsModal();
  };

  return (
    <>
      {showHeader && (
        <>
          <Header text="navbar:analytics">
            <TextField
              onChange={(e) => debouncedSearch(e)}
              disabled={isLoading}
              size="small"
              label={t("equimetre:search")}
            ></TextField>

            <Box display={"flex"} flexWrap={"wrap"} gap={1}>
              <Button
                onClick={goFullScreen}
                variant="outlined"
                startIcon={<Icon icon={"iconamoon:screen-full"}></Icon>}
              >
                {t("button:fullScreen")}
              </Button>
              <ExcelExports
                previousExports={filters?.previous_exports}
                viewName={view?.name}
                viewId={view?.["@id"]}
                onRefresh={refetchFilters}
                isFteching={isFetching}
                initialDateStart={analyticData.date_start}
                initialDateEnd={analyticData.date_end}
                filters={analyticData}
              />
            </Box>
          </Header>
          <Divider sx={{ my: 2 }}></Divider>
          <TrialAnalyticsBanner
            expirationDate={expirationDate}
            isTrialEnded={isTrialEnded}
            isTrialActif={isTrialActif}
          ></TrialAnalyticsBanner>
        </>
      )}

      <div id="analytics_content">
        <Filters
          saveViewNewParam={updateViewSetting}
          isViewUpdating={updateView.isLoading}
          isTrialEnded={isTrialEnded}
          resetFilters={setFilterForAnalytics}
          loading={filtersLoading}
          batchUpdate={batchUpdateFilters}
          updateAnalyticData={(items, key) => updateFilters(key, items)}
          analyticData={analyticData}
          currentView={view}
          isEditEnabled={isEditEnabled}
          openNewParamsModal={openNewParamsModal}
          disableEdit={disableEdit}
          handleCustomValue={handleCustomValue}
          filters={filters}
        ></Filters>
        <MaxRecordsBanner open={recordsBanner}></MaxRecordsBanner>
        <ConfigTabs
          openNewParamsModal={openNewParamsModal}
          isDeleting={deleteView.isLoading}
          toggleDeleteModal={toggleDeleteModal}
          updateName={handleUpdateViewName}
          isNameUpdating={updateView.isLoading}
          dataLoading={fetchAnalyticData.isLoading || filtersLoading}
          updateFilter={updateFilters}
          view={view}
          analyticsData={analyticsData}
          loading={isLoading}
          setView={setSelectedViewData}
          analyticViews={AnalyticViews}
          selectedParams={selectedParams}
          updateSelectedParams={handleAnalyticsParams}
          isEditEnabled={isEditEnabled}
          disableEdit={disableEdit}
          moreThan1000={recordsBanner}
          isInitialLoad={isInitialLoad}
          selectedHorse={selectedHorse}
          OnCreateNewView={OnCreateNewView}
        />
      </div>

      {/* Modals */}
      <DeleteModal
        handleClose={closeDeleteModal}
        open={openDeleteModal}
        onDelete={handleDeleteCurrentView}
        title="deleteConfiguration"
        subtitle="deleteConfigurationMessage"
        isDeleting={deleteView.isLoading}
      ></DeleteModal>

      {isEditEnabled && (
        <Dialog
          fullScreen
          open={openParamsModal}
          onClose={closeParamsModal}
          container={isFullscreen ? analyticsEl : document.body}
        >
          <AppBar sx={{ position: "sticky", backgroundColor: "var(--darkBg)" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={closeParamsModal}
                aria-label="close"
              >
                <GridCloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {t(
                  isCreatingNewView
                    ? "newConfiguration"
                    : "analytics:customize",
                )}
              </Typography>
              {/* On creating a new view we want to hide duplication and favorite */}
              {!isCreatingNewView && (
                <Box alignSelf={"center"} display={"flex"} gap={1}>
                  <LoadingButton
                    loading={saveNewConfig.isLoading}
                    variant="outlined"
                    onClick={onDuplicate}
                    startIcon={<Icon icon="ion:duplicate-outline"></Icon>}
                  >
                    {t("tracks:duplicate")}
                  </LoadingButton>
                  <LoadingButton
                    startIcon={
                      default_view === view?.["@id"] ? (
                        <FavoriteOutlined color="error"></FavoriteOutlined>
                      ) : (
                        <FavoriteBorderRounded></FavoriteBorderRounded>
                      )
                    }
                    variant="outlined"
                    onClick={setAsFavourite}
                  >
                    {t("layout:help:markConfigurationAsDefault")}
                  </LoadingButton>
                </Box>
              )}
            </Toolbar>
          </AppBar>
          <Stack direction={"column"} spacing={1} p={2} pb={0}>
            <ConfigAction
              updateName={handleSetViewName}
              view={view_name}
            ></ConfigAction>
            <EditConfig
              updatePin={onUpdatePin}
              pinnedParams={pinnedParams}
              isUpdating={updateView.isLoading || saveNewConfig.isLoading}
              selectedParams={selectedParams}
              onUpdateParam={onUpdateParam}
              allParams={allParams}
              groupedParams={groupedParams}
              loading={getParameters.isLoading}
              onParamCheck={onParamCheck}
              saveSettings={saveParams}
            />
            <DialogActions
              sx={{
                position: "sticky", // stays visible when scrolling DialogContent
                bottom: 0,
                zIndex: 1,
                borderTop: "1px solid var(--active)",
                backgroundColor: "var(--darkBg)",
              }}
            >
              <Stack direction={"row"} spacing={2} sx={{ alignSelf: "end" }}>
                {!isCreatingNewView && (
                  <LoadingButton
                    variant="outlined"
                    onClick={openDeleteModalHandler}
                    startIcon={<Delete></Delete>}
                  >
                    {t("button:delete")}
                  </LoadingButton>
                )}

                <LoadingButton
                  loading={updateView.isLoading || saveNewConfig.isLoading}
                  autoFocus
                  variant="contained"
                  onClick={saveParams}
                >
                  {t("button:save")}
                </LoadingButton>
              </Stack>
            </DialogActions>
          </Stack>
        </Dialog>
      )}
    </>
  );
}

export default Analytics;
